/* eslint-disable s4l/require-display-name */
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Button, Typography } from '@s4l/ds'
import { IconArrowDown } from '@s4l/icons/IconArrowDown'
import { IconArrowUp } from '@s4l/icons/IconArrowUp'
import { IconInfo } from '@s4l/icons/IconInfo'
import { Devices, useBrowser } from '@s4l/utils/hooks/useBrowser'
import styles from '../styles.module.css'
import TaxFeesDetails from '../TaxFeesDetails'
import { type RoomData, type TaxDetail } from '../types'
import { groupTaxDetails, getTotalTaxAmount, getAllTaxDetails } from '../utils'
import { usePriceDetailsHelpers } from '../hooks'

export interface RoomSectionProps {
  room: RoomData
  nights: number
}

// Tax info button that triggers the TaxFeesDetails modal
interface TaxButtonProps {
  taxDetails: TaxDetail[]
  label: string
}

function TaxButton({ taxDetails, label }: Readonly<TaxButtonProps>) {
  return (
    <TaxFeesDetails taxDetails={taxDetails}>
      <Button variant="text-flat" size="extraSmall" trailingIcon={<IconInfo size={16} />}>
        <Typography variant="body" size="l" bold>
          {label}
        </Typography>
      </Button>
    </TaxFeesDetails>
  )
}
TaxButton.displayName = 'TaxButton'

export default function RoomSection({ room, nights }: Readonly<RoomSectionProps>) {
  const { getTaxDisplayName, formatCurrency, formatMessage } = usePriceDetailsHelpers()
  const { device } = useBrowser()

  const isDesktop = device === Devices.Desktop

  // Collapsible state - open by default on desktop
  const [isTaxesOpen, setIsTaxesOpen] = useState(isDesktop)

  useEffect(() => {
    setIsTaxesOpen(isDesktop)
  }, [isDesktop])

  const handleTaxToggle = useCallback(() => {
    setIsTaxesOpen(prev => !prev)
  }, [])

  const pricePerNight = room.subtotal / nights
  const pricePerNightFormatted = formatCurrency(pricePerNight, room.subtotalCurrency)

  const allTaxDetails = useMemo(
    () => getAllTaxDetails(room.taxDetails, room.additionalFees),
    [room.taxDetails, room.additionalFees]
  )
  const groupedTaxDetails = useMemo(() => groupTaxDetails(allTaxDetails), [allTaxDetails])

  // Only show tax section if we have taxes AND they have actual amounts > 0
  const hasTaxDetails =
    groupedTaxDetails.length > 0 &&
    groupedTaxDetails.some(tax => (tax.client?.amount ?? tax.amount) > 0)

  const taxLabel = formatMessage({ id: 'global.taxes-fees' })
  const totalTaxAmount = formatCurrency(getTotalTaxAmount(allTaxDetails), room.totalCurrency)

  const taxDetailsContent = groupedTaxDetails.map(
    tax =>
      (tax.client?.amount || tax.amount > 0) && (
        <div className={styles.detailsRow} key={tax.name}>
          <Typography variant="caption" size="l" color="var(--color-neutral-600)">
            {`${getTaxDisplayName(tax.name)} (${tax.included ? formatMessage({ id: 'tax-breakdown.pay-upon-booking' }) : formatMessage({ id: 'tax-breakdown.pay-at-hotel' })})`}
          </Typography>
          <Typography variant="caption" size="l" color="var(--color-neutral-600)">
            {formatCurrency(tax.client?.amount ?? tax.amount, tax.client?.currency ?? tax.currency)}
          </Typography>
        </div>
      )
  )

  // Render room header - "X nights x $price" → "$total"
  const renderRoomHeader = () => {
    return (
      <div className={styles.roomHeader}>
        <Typography variant="body" size="l">
          {`${formatMessage({ id: 'global.nights' }, { nights })} x ${pricePerNightFormatted}`}
        </Typography>
        <Typography variant="body" size="l">
          {formatCurrency(room.subtotal, room.subtotalCurrency)}
        </Typography>
      </div>
    )
  }
  renderRoomHeader.displayName = 'renderRoomHeader'

  // Render tax section based on device
  const renderTaxSection = () => {
    if (!hasTaxDetails) {
      return (
        <div className={styles.detailsBlock}>
          <div className={styles.detailsRowTitle}>
            <Typography variant="body" size="l">
              {taxLabel}
            </Typography>
            <Typography variant="body" size="l">
              {formatCurrency(0, room.totalCurrency)}
            </Typography>
          </div>
        </div>
      )
    }

    // Desktop: Use collapsible with smooth animation
    if (isDesktop) {
      return (
        <div className={styles.v3TaxSection}>
          <div className={styles.detailsRowTitle}>
            <button
              type="button"
              className={styles.v3TaxButton}
              onClick={handleTaxToggle}
              aria-expanded={isTaxesOpen}
            >
              <Typography variant="body" size="l" bold>
                {taxLabel}
              </Typography>
              {isTaxesOpen ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
            </button>
            <Typography variant="body" size="l">
              {totalTaxAmount}
            </Typography>
          </div>
          <div className={`${styles.collapsibleWrapper} ${isTaxesOpen ? styles.open : ''}`}>
            <div className={styles.collapsibleContent}>{taxDetailsContent}</div>
          </div>
        </div>
      )
    }

    // Mobile: Use TaxFeesDetails button+modal (no sub-taxes shown below)
    return (
      <div className={styles.v3TaxSection}>
        <div className={styles.detailsRowTitle}>
          <TaxButton taxDetails={groupedTaxDetails} label={taxLabel} />
          <Typography variant="body" size="l">
            {totalTaxAmount}
          </Typography>
        </div>
      </div>
    )
  }
  renderTaxSection.displayName = 'renderTaxSection'

  return (
    <div className={styles.room}>
      {renderRoomHeader()}
      {renderTaxSection()}
    </div>
  )
}

RoomSection.displayName = 'RoomSection'
